import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Base_URL2 } from '../url';
import '../Styling/AzureInvoice_taxReport.css';

// ════════════════════════════════════════════
// API LAYER
// ════════════════════════════════════════════
const AIT_BASE = `${Base_URL2}/vim-invoiceUpload`;

const aitUploadInvoice  = (file) => {
    const form = new FormData();
    form.append('file', file);
    return axios.post(`${AIT_BASE}/upload`, form);
};

const aitGetInvoices    = () => axios.get(AIT_BASE);
const aitAnalyzeInvoice = (id) => axios.post(`${AIT_BASE}/${id}/analyze`);
const aitGetFileUrl     = (id) => `${AIT_BASE}/${id}/file`;


// ════════════════════════════════════════════
// HELPERS
// ════════════════════════════════════════════

// Currency symbol map — fallback to ₹ if null
const getCurrencySymbol = (code) => {
    const map = { INR: '₹', USD: '$', EUR: '€', GBP: '£' };
    return map[code] || '₹';
};

const fmtCurrency = (val, code = null) => {
    if (val == null) return '—';
    const symbol = getCurrencySymbol(code);
    return `${symbol}${Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
};

const fmtDate = (val) => {
    if (!val) return '—';
    return new Date(val).toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric'
    });
};

// Renders address with \n as line breaks
const AddressBlock = ({ value }) => {
    if (!value) return null;
    return (
        <div className="ait-rs-address">
            {value.split('\n').map((line, i) => (
                <span key={i}>{line}</span>
            ))}
        </div>
    );
};


// ════════════════════════════════════════════
// COMPONENT 1: RESULT PANEL
// ════════════════════════════════════════════
const AzureInvoice_Report_Result = ({ data }) => {

    if (!data) return (
        <div className="ait-panel-right">
            <div className="ait-rs-header">🔍 Extracted Fields</div>
            <div className="ait-rs-empty">
                <span className="ait-rs-empty-icon">📊</span>
                <span>Run analysis to see<br />extracted fields</span>
            </div>
        </div>
    );

    const cc = data.currencyCode;

    return (
        <div className="ait-panel-right">
            <div className="ait-rs-header">🔍 Extracted Fields</div>
            <div className="ait-rs-content">

                {/* ── Invoice Meta ── */}
                <div className="ait-rs-section-title">Invoice Info</div>

                {data.invoiceId && (
                    <div className="ait-rs-field">
                        <div className="ait-rs-field-label">Invoice #</div>
                        <div className="ait-rs-field-value">{data.invoiceId}</div>
                    </div>
                )}
                {data.invoiceDate && (
                    <div className="ait-rs-field">
                        <div className="ait-rs-field-label">Invoice Date</div>
                        <div className="ait-rs-field-value">{fmtDate(data.invoiceDate)}</div>
                    </div>
                )}
                {data.dueDate && (
                    <div className="ait-rs-field">
                        <div className="ait-rs-field-label">Due Date</div>
                        <div className="ait-rs-field-value">{fmtDate(data.dueDate)}</div>
                    </div>
                )}
                {data.paymentTerm && (
                    <div className="ait-rs-field">
                        <div className="ait-rs-field-label">Terms</div>
                        <div className="ait-rs-field-value">{data.paymentTerm}</div>
                    </div>
                )}
                {data.purchaseOrder && (
                    <div className="ait-rs-field">
                        <div className="ait-rs-field-label">P.O. #</div>
                        <div className="ait-rs-field-value">{data.purchaseOrder}</div>
                    </div>
                )}
                {data.currencyCode && (
                    <div className="ait-rs-field">
                        <div className="ait-rs-field-label">Currency</div>
                        <div className="ait-rs-field-value">{data.currencyCode}</div>
                    </div>
                )}

                <hr className="ait-rs-divider" />

                {/* ── Vendor ── */}
                <div className="ait-rs-section-title">Vendor</div>

                {data.vendorName && (
                    <div className="ait-rs-field">
                        <div className="ait-rs-field-label">Name</div>
                        <div className="ait-rs-field-value">{data.vendorName}</div>
                    </div>
                )}
                {data.vendorAddress && (
                    <div className="ait-rs-field">
                        <div className="ait-rs-field-label">Address</div>
                        <AddressBlock value={data.vendorAddress} />
                    </div>
                )}
                {data.vendorTaxId && (
                    <div className="ait-rs-field">
                        <div className="ait-rs-field-label">GSTIN</div>
                        <div className="ait-rs-field-value ait-rs-mono">{data.vendorTaxId}</div>
                    </div>
                )}

                <hr className="ait-rs-divider" />

                {/* ── Customer ── */}
                <div className="ait-rs-section-title">Bill To</div>

                {data.customerName && (
                    <div className="ait-rs-field">
                        <div className="ait-rs-field-label">Name</div>
                        <div className="ait-rs-field-value">{data.customerName}</div>
                    </div>
                )}

                {/* customerAddress — renders once backend fix is live */}
                {data.customerAddress && (
                    <div className="ait-rs-field">
                        <div className="ait-rs-field-label">Address</div>
                        <AddressBlock value={data.customerAddress} />
                    </div>
                )}

                {data.customerTaxId && (
                    <div className="ait-rs-field">
                        <div className="ait-rs-field-label">GSTIN</div>
                        <div className="ait-rs-field-value ait-rs-mono">{data.customerTaxId}</div>
                    </div>
                )}

                <hr className="ait-rs-divider" />

                {/* ── GST Details ── */}
                {data.gstDetails && (
                    <>
                        <div className="ait-rs-section-title">GST Details</div>

                        {data.gstDetails.placeOfSupply && (
                            <div className="ait-rs-field">
                                <div className="ait-rs-field-label">Place of Supply</div>
                                <div className="ait-rs-field-value">{data.gstDetails.placeOfSupply}</div>
                            </div>
                        )}

                        {data.gstDetails.igstRate != null && (
                            <div className="ait-rs-field">
                                <div className="ait-rs-field-label">IGST Rate</div>
                                <div className="ait-rs-field-value ait-rs-gst-rate">{data.gstDetails.igstRate}%</div>
                            </div>
                        )}
                        {data.gstDetails.igstAmount != null && (
                            <div className="ait-rs-field">
                                <div className="ait-rs-field-label">IGST Amount</div>
                                <div className="ait-rs-field-value">{fmtCurrency(data.gstDetails.igstAmount, cc)}</div>
                            </div>
                        )}

                        {data.gstDetails.cgstRate != null && (
                            <div className="ait-rs-field">
                                <div className="ait-rs-field-label">CGST Rate</div>
                                <div className="ait-rs-field-value ait-rs-gst-rate">{data.gstDetails.cgstRate}%</div>
                            </div>
                        )}
                        {data.gstDetails.cgstAmount != null && (
                            <div className="ait-rs-field">
                                <div className="ait-rs-field-label">CGST Amount</div>
                                <div className="ait-rs-field-value">{fmtCurrency(data.gstDetails.cgstAmount, cc)}</div>
                            </div>
                        )}

                        {data.gstDetails.sgstRate != null && (
                            <div className="ait-rs-field">
                                <div className="ait-rs-field-label">SGST Rate</div>
                                <div className="ait-rs-field-value ait-rs-gst-rate">{data.gstDetails.sgstRate}%</div>
                            </div>
                        )}
                        {data.gstDetails.sgstAmount != null && (
                            <div className="ait-rs-field">
                                <div className="ait-rs-field-label">SGST Amount</div>
                                <div className="ait-rs-field-value">{fmtCurrency(data.gstDetails.sgstAmount, cc)}</div>
                            </div>
                        )}

                        <hr className="ait-rs-divider" />
                    </>
                )}

                {/* ── Bank Details ── */}
{data.bankDetails && (
    <>
        <div className="ait-rs-section-title">Bank Details</div>

        {data.bankDetails.bankName && (
            <div className="ait-rs-field">
                <div className="ait-rs-field-label">Bank</div>
                <div className="ait-rs-field-value">{data.bankDetails.bankName}</div>
            </div>
        )}
        {data.bankDetails.accountNumber && (
            <div className="ait-rs-field">
                <div className="ait-rs-field-label">Account No.</div>
                <div className="ait-rs-field-value ait-rs-mono">
                    {data.bankDetails.accountNumber}
                </div>
            </div>
        )}
        {data.bankDetails.ifscCode && (
            <div className="ait-rs-field">
                <div className="ait-rs-field-label">IFSC</div>
                <div className="ait-rs-field-value ait-rs-mono">
                    {data.bankDetails.ifscCode}
                </div>
            </div>
        )}

        <hr className="ait-rs-divider" />
    </>
)}
{/* ── Digital Signature ── */}
{data.digitalSignature && (
    <>
        <div className="ait-rs-section-title">Digital Signature</div>

        {data.digitalSignature.signedBy && (
            <div className="ait-rs-field">
                <div className="ait-rs-field-label">Signed By</div>
                <div className="ait-rs-field-value">{data.digitalSignature.signedBy}</div>
            </div>
        )}
        {data.digitalSignature.signedAt && (
            <div className="ait-rs-field">
                <div className="ait-rs-field-label">Signed At</div>
                <div className="ait-rs-field-value">
                    {new Date(data.digitalSignature.signedAt).toLocaleString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    })}
                </div>
            </div>
        )}

        <hr className="ait-rs-divider" />
    </>
)}

                {/* ── Line Items ── */}
                {data.items?.length > 0 && (
                    <>
                        <div className="ait-rs-section-title">Line Items</div>
                        {data.items.map((item, i) => (
                            <div className="ait-rs-lineitem-card" key={i}>
                                <div className="ait-rs-lineitem-desc" title={item.description}>
                                    {item.description || '—'}
                                </div>
                                <div className="ait-rs-lineitem-meta">
                                    {item.productCode && (
                                        <span className="ait-rs-lineitem-hsn">HSN: {item.productCode}</span>
                                    )}
                                    {item.quantity != null && (
                                        <span>Qty: {item.quantity}</span>
                                    )}
                                    {item.unitPrice != null && (
                                        <span>Rate: {fmtCurrency(item.unitPrice, cc)}</span>
                                    )}
                                    {item.tax != null && (
                                        <span className="ait-rs-tax-pill">
                                            Tax{data.gstDetails?.igstRate != null
                                                ? ` (IGST ${data.gstDetails.igstRate}%)`
                                                : data.gstDetails?.cgstRate != null
                                                    ? ` (CGST+SGST ${data.gstDetails.cgstRate}%+${data.gstDetails.sgstRate}%)`
                                                    : ''
                                            }: {fmtCurrency(item.tax, cc)}
                                        </span>
                                    )}
                                </div>
                                <div className="ait-rs-lineitem-amount">
                                    {fmtCurrency(item.amount, cc)}
                                </div>
                            </div>
                        ))}
                    </>
                )}

                {/* ── Totals ── */}
                <div className="ait-rs-totals">
                    {data.subTotal != null && (
                        <div className="ait-rs-total-row">
                            <span>Subtotal</span>
                            <span>{fmtCurrency(data.subTotal, cc)}</span>
                        </div>
                    )}
                    {data.totalTax != null && (
                        <div className="ait-rs-total-row">
                            <span>
                                {data.gstDetails?.igstAmount != null && data.gstDetails?.cgstAmount == null
                                    ? `IGST (${data.gstDetails.igstRate}%)`
                                    : data.gstDetails?.cgstAmount != null
                                        ? 'CGST + SGST'
                                        : 'Tax'}
                            </span>
                            <span>{fmtCurrency(data.totalTax, cc)}</span>
                        </div>
                    )}
                    {data.invoiceTotal != null && (
                        <div className="ait-rs-total-row ait-rs-total-row--grand">
                            <span>Invoice Total</span>
                            <span>{fmtCurrency(data.invoiceTotal, cc)}</span>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
};


// ════════════════════════════════════════════
// COMPONENT 2: VIEWER PANEL
// ════════════════════════════════════════════
const AzureInvoice_Report_Viewer = ({ invoice, onAnalysis }) => {
    const [loading, setLoading] = useState(false);
    const [error,   setError]   = useState('');

    const handleAnalyze = async () => {
        setLoading(true);
        setError('');
        try {
            const { data } = await aitAnalyzeInvoice(invoice.id);
            onAnalysis(data);
        } catch {
            setError('Analysis failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (!invoice) return (
        <div className="ait-panel-center">
            <div className="ait-vw-empty">
                <span className="ait-vw-empty-icon">📂</span>
                <span>Select an invoice from the left panel</span>
            </div>
        </div>
    );

    return (
        <div className="ait-panel-center">
            <div className="ait-vw-toolbar">
                <span className="ait-vw-filename" title={invoice.fileName}>
                    📄 {invoice.fileName}
                </span>
                <button
                    className="ait-vw-analyze-btn"
                    onClick={handleAnalyze}
                    disabled={loading}
                >
                    {loading
                        ? <><span className="ait-spinner" />Analyzing...</>
                        : '▶ Run Analysis'
                    }
                </button>
            </div>

            {error && <p className="ait-error">{error}</p>}

            <iframe
                key={invoice.id}
                src={aitGetFileUrl(invoice.id)}
                className="ait-vw-iframe"
                title="Invoice Preview"
            />
        </div>
    );
};


// ════════════════════════════════════════════
// COMPONENT 3: UPLOADER / LIST PANEL
// ════════════════════════════════════════════
const AzureInvoice_Report_Uploader = ({ onSelect, selectedId }) => {
    const [invoices,  setInvoices]  = useState([]);
    const [uploading, setUploading] = useState(false);
    const [error,     setError]     = useState('');
    const fileRef = useRef();

    const fetchList = async () => {
        try {
            const { data } = await aitGetInvoices();
            setInvoices(data);
        } catch {
            setError('Failed to load invoices.');
        }
    };

    useEffect(() => { fetchList(); }, []);

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        e.target.value = '';
        setUploading(true);
        setError('');
        try {
            await aitUploadInvoice(file);
            await fetchList();
        } catch {
            setError('Upload failed. Please try again.');
        } finally {
            setUploading(false);
        }
    };

    const formatDate = (dateStr) =>
        new Date(dateStr).toLocaleDateString('en-IN', {
            month: 'short', day: 'numeric', year: 'numeric'
        });

    return (
        <div className="ait-panel-left">
            <div className="ait-up-header">
                <h2 className="ait-up-title">📄 Invoices</h2>
                <input
                    type="file"
                    ref={fileRef}
                    style={{ display: 'none' }}
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={handleUpload}
                />
                <button
                    className="ait-up-btn"
                    onClick={() => fileRef.current.click()}
                    disabled={uploading}
                >
                    {uploading
                        ? <><span className="ait-spinner" />Uploading...</>
                        : '+ Upload Invoice'
                    }
                </button>
                {error && <p className="ait-up-error">{error}</p>}
            </div>

            <div className="ait-up-list">
                {invoices.length === 0 ? (
                    <p className="ait-up-empty">
                        No invoices yet.<br />Upload one above.
                    </p>
                ) : (
                    invoices.map(inv => (
                        <div
                            key={inv.id}
                            className={`ait-up-item ${selectedId === inv.id ? 'ait-up-item--active' : ''}`}
                            onClick={() => onSelect(inv)}
                        >
                            <div className="ait-up-item-name" title={inv.fileName}>
                                {inv.fileName}
                            </div>
                            <div className="ait-up-item-meta">
                                <span>{formatDate(inv.uploadedAt)}</span>
                                <span className={`ait-up-badge ait-up-badge--${inv.status.toLowerCase()}`}>
                                    {inv.status}
                                </span>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};


// ════════════════════════════════════════════
// MAIN EXPORT
// ════════════════════════════════════════════
const AzureInvoice_Report = () => {
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [analysisData,    setAnalysisData]    = useState(null);

    const handleSelect = (inv) => {
        setSelectedInvoice(inv);
        setAnalysisData(null);
    };

    return (
        <div className="ait-root">
            <div className="ait-app">
                <AzureInvoice_Report_Uploader
                    onSelect={handleSelect}
                    selectedId={selectedInvoice?.id}
                />
                <AzureInvoice_Report_Viewer
                    invoice={selectedInvoice}
                    onAnalysis={setAnalysisData}
                />
                <AzureInvoice_Report_Result
                    data={analysisData}
                />
            </div>
        </div>
    );
};

export default AzureInvoice_Report;